import { client } from '@/utils/sanity-client';
import { SearchOutlined, WarningOutlined } from '@ant-design/icons';
import { AutoComplete, Input, Space, Spin, Tag, message } from 'antd';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

const HelpCenterSearch = ({ onSearch, onCategoryChange }) => {
  const router = useRouter();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState(null);

  const categories = [
    { value: 'all', label: 'All', color: '#108ee9' },
    { value: 'articles', label: 'Articles', color: '#05c168' },
    { value: 'faq', label: 'FAQs', color: '#1890ff' },
    { value: 'caseStudies', label: 'Case Studies', color: '#f50' },
    { value: 'guides', label: 'Guides', color: '#722ed1' },
    { value: 'industryNews', label: 'Industry News', color: '#eb2f96' },
  ];

  const searchContent = async (searchText) => {
    if (!searchText) {
      setOptions([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Basic sanitization
      const sanitizedSearchText = searchText.trim().replace(/[*+~.()'"!:@]/g, ' ');

      // Build type filter
      let typeFilter =
        selectedCategory === 'all'
          ? '_type in ["articles", "faq", "caseStudies", "guides", "industryNews"]'
          : `_type == "${selectedCategory}"`;

      // Simpler search query
      const query = `*[${typeFilter} && (
        title match "*${sanitizedSearchText}*" ||
        pt::text(body) match "*${sanitizedSearchText}*" ||
        coalesce(question, "") match "*${sanitizedSearchText}*" ||
        coalesce(answer, "") match "*${sanitizedSearchText}*"
      )] {
        _id,
        _type,
        title,
        question,
        answer,
        "slug": slug.current,
        "preview": select(
          defined(body) => pt::text(body[0...150]),
          defined(answer) => answer[0...150],
          ''
        ),
        "estimatedReadTime": round(length(pt::text(body)) / 1000 * 0.2)
      } | order(coalesce(title, question) asc)[0...5]`;

      console.log('[Search] Executing query:', {
        text: sanitizedSearchText,
        category: selectedCategory,
      });

      const results = await client.fetch(query);

      if (!results) {
        throw new Error('No results returned from query');
      }

      const formattedOptions = results.map((item) => ({
        value: item.slug,
        label: (
          <div style={{ padding: '8px 0' }}>
            <div style={{ fontWeight: 500 }}>
              {item.title || item.question}
              <Tag
                color={categories.find((cat) => cat.value === item._type)?.color}
                style={{ marginLeft: 8 }}
              >
                {categories.find((cat) => cat.value === item._type)?.label}
              </Tag>
            </div>
            {item.preview && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                {item.preview.length > 150 ? `${item.preview.substring(0, 150)}...` : item.preview}
              </div>
            )}
          </div>
        ),
        type: item._type,
        slug: item.slug,
      }));

      setOptions(
        formattedOptions.length
          ? formattedOptions
          : [
              {
                value: 'no-results',
                label: (
                  <div style={{ color: '#666' }}>
                    No results found. Try different keywords or categories.
                  </div>
                ),
              },
            ]
      );
    } catch (error) {
      console.error('[Search] Error:', {
        message: error.message,
        category: selectedCategory,
        searchText,
      });

      setError(error);
      setOptions([
        {
          value: 'error',
          label: (
            <div style={{ color: '#ff4d4f', padding: '8px 0' }}>
              <WarningOutlined style={{ marginRight: 8 }} />
              Unable to perform search. Please try again.
            </div>
          ),
        },
      ]);

      message.error('Search failed. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  // Native debounce implementation
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          searchContent(value);
        }, 300);
      };
    })(),
    []
  );

  const handleSearch = (value) => {
    debouncedSearch(value);
  };

  const handleSelect = (value, option) => {
    if (option.value === 'no-results' || option.value === 'error') {
      return; // Don't do anything for these special cases
    }

    // Map the type to the correct URL path if needed
    const typeUrlMap = {
      faq: 'faq',
      caseStudies: 'case-studies',
      industryNews: 'industry-news',
    };

    // Get the correct path segment for the content type
    const typePathSegment = typeUrlMap[option.type] || option.type;

    // Construct the URL using just the type and slug
    const url = `/${typePathSegment}/${option.slug}`;

    console.log('[Search] Navigating to:', url);

    // Use Next.js router to navigate
    router.push(url).catch((err) => {
      console.error('[Search] Navigation error:', err);
      message.error('Failed to navigate to the selected content.');
    });

    if (onSearch) {
      onSearch(option);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    onCategoryChange(category); // Notify parent component about category change
  };

  return (
    <div className="help-center-search">
      <motion.div
        className="search-categories"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Space
          size={[8, 16]}
          wrap
          align="center"
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {categories.map((category) => (
            <motion.div
              key={category.value}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Tag
                className={`category-pill ${selectedCategory === category.value ? 'selected' : ''}`}
                color={selectedCategory === category.value ? category.color : 'default'}
                style={{
                  cursor: 'pointer',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '16px',
                  fontFamily: 'Clarity City, sans-serif',
                  margin: '4px',
                  border: selectedCategory === category.value ? 'none' : '1px solid #d9d9d9',
                  boxShadow:
                    selectedCategory === category.value ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => handleCategoryChange(category.value)}
              >
                {category.label}
              </Tag>
            </motion.div>
          ))}
        </Space>
      </motion.div>

      <div style={{ marginTop: '32px' }}>
        <AutoComplete
          dropdownMatchSelectWidth={true}
          style={{ width: '100%' }}
          options={options}
          onSelect={handleSelect}
          onSearch={handleSearch}
        >
          <Input
            size="large"
            placeholder={`Search ${selectedCategory === 'all' ? 'everything' : categories.find((c) => c.value === selectedCategory)?.label.toLowerCase()}...`}
            prefix={loading ? <Spin size="small" /> : <SearchOutlined />}
            style={{
              borderRadius: '12px',
              padding: '12px',
              fontSize: '16px',
              fontFamily: 'Clarity City, sans-serif',
            }}
          />
        </AutoComplete>
      </div>
    </div>
  );
};

export default HelpCenterSearch;
